import { isAssistantPilotEnvironment } from '@common-components/assistant-start/availability';
import { AssistantStartPage } from '@common-components/assistant-start/page';
import { AssistantGmailHandoffPage } from '@common-components/assistant-gmail-handoff/page';
import { AssistantPilotSetupPage } from '@common-components/assistant-pilot-setup/page';
import { appConfig } from '@common/config/app.config';
import { SessionTimeline } from '@/website/views/session-events-timeline/session-timeline/session-timeline';
import { AllPlansDemo } from '@/components/demo/SubscriptionPlanDemo';
import { FormState } from "@common-components/access-form/access-form";
import BTThemeProvider from "@common-components/bt-theme-provider";
import { Toaster } from "@components/ui/toaster";
import ConditionalWrapper from "@common-components/conditional-wrapper/ConditionalWrapper";
import GuardedRoute from "@common-components/guarded-route/guarded-route";
import ClearFlowChrome from "@common-components/clear-user-menu/clear-flow-chrome";
import { flowPath } from "@common/utils/clear-flow";
import InviteUserModal from "@common-components/invite/InviteUserModal";
import TrialModal from "@common-components/modals/trial-modal";
import { OAuthTest } from "@common-components/oauth-test";
import { MCPOAuthPage } from "@common-components/mcp-oauth-page";
import { MCPOAuthCallbackPage } from "@common-components/mcp-oauth-callback-page";
import PaymentThankYouPopup from "@common-components/payment-form/payment-thank-you-popup";
import NotificationsDrawer from "@common-components/tasks/extraData/notifications/notifications-drawers";
import { useAnalyticsService } from "@common-reducers/hooks/use-analytics-service";
import { AnalyticsService } from "@common-services/analytics-service";

import { globalHistory } from "@common-services/global-history";
import AccountInvoiceView from "@common-views/account-invoice-view/account-invoice-view";
import AccountLangView from "@common-views/account-lang/account-lang";
import AccountNotificationsView from "@common-views/account-notification/account-notification";
import AccountSecurityView from "@common-views/account-security/account-security";
import AccountBackupView from "@common-views/account-backup/account-backup";
import AccountSubscriptionView from "@common-views/account-subscription-view/account-subscription-view";
import AccountView from "@common-views/account-view/account-view";
import AccountWorkspacesView from "@common-views/account-workspaces/account-workspaces";
import AdminAPIView from "@common-views/admin-api/admin-api";
import AdminBillingView from "@common-views/admin-billing/admin-billing";
import ApiDocumentationView from "@common-views/api-documentation-view/api-documentation-view";
import CalendarView from "@common-views/calendar-view/calendar-view";
import WhatsappView from '@common-views/whatsapp-view/whatsapp-view';
import CreditsUsageView from "@common-views/credits-usage-view/credits-usage-view";
import ReferView from "@common-views/refer-view/refer-view";
import EngineExplorer from "@common-views/engine-explorer/engine-explorer";
import ForbiddenAccessView from "@common-views/forbidden-access-view/forbidden-access-view";
import FormPreviewView from "@common-views/form-preview/form-preview-view";
import InvoiceView from "@common-views/invoice-view/invoice-view";
import JoinBoard from "@common-views/join-board/join-board";
import JoinTaskBoard from "@common-views/join-task-board/join-task-board";
import JoinWorkSpace from "@common-views/join-workspace/join-workspace";
import LoginView from "@common-views/login-view/login-view";
import PageNotFound from "@common-views/page-not-found/page-not-found";
import QrView from "@common-views/qr-view/qr-view";
import ResetPasswordView from "@common-views/reset-password-view/reset-password-view";
import SubscriptionView from "@common-views/subscription-view/subscription-view";
import TaskView from "@common-views/task-view/task-view";
import ThankYouView from "@common-views/thank-you-view/thank-you-view";
import { UnifiedAdminBilling } from "@common-views/unified-admin-billing/unified-admin-billing";
import UninstallView from "@common-views/uninstall-view/uninstall-view";
import UserManagementView from "@common-views/user-management/user-management";
import UserOverview from "@common-views/user-overview/user-overview";
import WebHookGuideView from "@common-views/webbook-guide-view/webbook-guide-view";
import WebHookGuideMake from "@common-views/webhook-guide-make/webbook-guide-make";
import WebHookGuideZapier from "@common-views/webhook-guide-zaiper/webbook-guide-zapier";
import WorkspaceView from "@common-views/workspace-view/workspace-view";
import AllocatedPhoneNumbersPage from "@common/components/whatsapp-numbers/AllocatedPhoneNumbersPage";
import { CommonRootState } from "@common/types/common-root-state-type.d";
import { lazy, Suspense, useEffect } from "react";
import { unstable_HistoryRouter as HistoryRouter, Navigate, Route, Routes } from "react-router-dom";
import ClusterOptimizer from './views/cluster-optimizer/cluster-optimizer';
import K8sAnalyzer from './views/k8s-analyzer/k8s-analyzer';

import { injectPixels } from "@common/site/inject-pixels";
import CookieConsentBanner from "@common/components/cookie-consent/cookie-consent-banner";
import { captureOpenCrispIntent } from "@common-services/crisp-after-login";

import KnowledgeBaseDetail from "@common-components/knowledge-base/detail/KnowledgeBaseDetail";
import KnowledgeBaseView from "@common-views/knowledge-base-view/knowledge-base-view";
import "@common/color-scheme.css";
import { BotDetailView } from "@common/components/bt-chatbot/bot-display/BotDetailView";
import { BotTestChatView } from "@common/views/bot-test-chat/bot-test-chat-view";
import SessionDetailsView from "@common/components/bt-chatbot/bot-display/components/SessionDetailsView";
import BotListView from "@common/components/bt-chatbot/bot-list-view/BotListView";
import BTChatBotView from "@common/components/bt-chatbot/bt-chatbot-view";
import "@common/site/site.module.scss";
import { CampaignManagementView } from "@common/views/campaign-management-view/campaign-management-view";
import LaunchCampaignView from "@common/views/launch-campaign-view/launch-campaign-view";
import LaunchDripView from "@common/views/launch-drip-view/launch-drip-view";
import CampaignsView from "@extension/context/content/components/bt-bulk-dashboard/campaigns-view";
import CampaignsRedesignView from "@common-views/campaigns-redesign/campaigns-redesign-view";
import { CAMPAIGN_REDESIGN_PATHS, CLEAR_CAMPAIGN_REDESIGN_PATHS } from "@common-views/campaigns-redesign/campaigns-routes";
import "../styles/globals.css";
import "../styles/globals-website.css";
import "../styles/rtl.css";

import BotList from "@/pages/bot-list";
import BotUsageDashboard from "@/pages/bot-usage-dashboard/BotUsageDashboard";
import { SessionListPage } from '@/pages/session-list/SessionListPage';
import { EnginesManagementPage } from '@/pages/engines-management/EnginesManagementPage';
import { LegacyAgentEditRedirect, MyBridgePage } from '@/pages/my-bridge/MyBridgePage';
import AppView from '@common/components/app-view';
import { AccountPage } from '@common-components/account-page/account-page';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { K8sAnalyzerNodesView } from "./views/k8s-analyzer/k8s-analyzer-nodes-view";
import TeamInboxView from '@/components/team-inbox/team-inbox-view';
import ViewBackupView from '@/components/view-backup/view-backup-view';
import BtTeamInboxView from '@/components/team-inbox/BtTeamInboxView';
import KeepWhatsappAwakeView from '@common/views/keep-whatsapp-awake/keep-whatsapp-awake-view';
import { BTGPTView } from './views/btgpt';
import { ImportMessagesView } from './views/import-messages/import-messages-view';
import AiAssistant from '@common-components/ai-assistant/ai-assistant';
import TestComponentsView from './views/test-components/test-components';
import EditorPlayground from '@common-views/editor-playground';
import TestReminderPage from './views/test-reminder/TestReminderPage';
import MessagesDashboard from '@common-views/messages-dashboard-view/MessagesDashboard';
import DiagnosticsView from '@common-views/diagnostics-view/diagnostics-view';
import OnlineEnginesView from '@common-views/online-engines-view/online-engines-view';

// Lazy-load the Support view — heavy, rarely-hit route; keeps it out of the
// main website bundle (Round 5 perf audit, R5-B).
const Support = lazy(() => import("@common-views/support/support"));
const BridgesPage = lazy(() => import("../pages/my-bridge/BridgesPage"));
const OnboardingPage = lazy(() => import("../pages/onboarding/onboarding-page"));

export default function WebsiteComponent() {
  const analyticsService: AnalyticsService = useAnalyticsService();
  
  // Initialize automatic dark mode using darkreader
  // useDarkReader();

  useEffect(() => {
    // Capture an `?openCrisp=1` intent BEFORE injecting pixels —
    // Crisp's loader strips/rewrites the URL on init in some builds,
    // and the router can also rewrite the URL before we get a chance
    // to read it. Storing the flag in sessionStorage here makes it
    // survive both. Pickup happens in setAuthenticatedUser via
    // consumeOpenCrispIntentWithRetry (see backend-api-query-handlers).
    captureOpenCrispIntent();
    injectPixels()
  }, []);

  const sitePages = () => {
    const supportedUserId = new URLSearchParams(window.location.search).get('supportedUserId');
    const readOnly = supportedUserId ? true : false;
    return (
      <HelmetProvider>
        <Helmet>
          <title>Blueticks App</title>
          <meta property="og:title" content="Blueticks – Your WhatsApp Productivity Superpower" />
          <meta property="og:description" content="Schedule messages, launch campaigns, manage tasks, and automate support—all within WhatsApp Web." />
          <meta property="og:url" content="https://blueticks.co" />
          <meta property="og:type" content="website" />
          <meta property="og:image" content="/img/og-image.png" />
          <meta property="og:site_name" content="Blueticks" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Blueticks – Your WhatsApp Productivity Superpower" />
          <meta name="twitter:description" content="All your customer messaging, automation, and campaigns—inside WhatsApp." />
          <meta name="twitter:image" content="/img/og-image.png" />
        </Helmet>
        <BTThemeProvider>
          <div className="min-h-screen font-sans antialiased" style={{ background: 'var(--color-bg)', color: 'var(--color-text)' }}>
            <CookieConsentBanner />
            <HistoryRouter history={globalHistory} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <Routes>
                {isAssistantPilotEnvironment(appConfig.ENV) && <Route path="/assistant/start" element={<GuardedRoute element={<AssistantStartPage />} auth={true} redirectBack={true} />} />}
                {appConfig.ENV === 'assistant-native-pilot' && <Route path="/assistant/pilot/:workspaceId" element={<GuardedRoute element={<AssistantPilotSetupPage apiBaseUrl={appConfig.API_ENDPOINT} />} auth={true} redirectBack={true} />} />}
                {isAssistantPilotEnvironment(appConfig.ENV) && <Route path="/assistant/gmail/:customerId" element={<GuardedRoute element={<AssistantGmailHandoffPage apiBaseUrl={appConfig.API_ENDPOINT} />} auth={true} redirectBack={true} />} />}
                <Route path="/account" element={
                  <GuardedRoute
                    element={<AccountView readOnly={readOnly} />}
                    auth={true}
                  />
                } />
                <Route path="/account-subscription" element={
                  <GuardedRoute
                    element={<AccountSubscriptionView />}
                    auth={true}
                  />
                } />
                {/* The previous plans page, kept for side-by-side comparison after the redesign went live. */}
                <Route path="/account-subscription-test" element={
                  <GuardedRoute
                    element={<AccountSubscriptionView legacy />}
                    auth={true}
                  />
                } />
                <Route path="/account-invoices" element={
                  <GuardedRoute
                    element={<AccountInvoiceView />}
                    auth={true}
                  />
                } />
                <Route
                  path="/new-workspace"
                  element={
                    <GuardedRoute element={<WorkspaceView />} auth={true} />
                  }
                />
                <Route
                  path="/edit-workspace"
                  element={
                    <GuardedRoute element={<WorkspaceView editMode />} auth={true} />
                  }
                />
                <Route
                  path="/form-preview/:shareId"
                  element={<FormPreviewView />}
                />
                <Route
                  path="/admin-billing-old"
                  element={
                    <GuardedRoute element={<AdminBillingView readOnly={readOnly} />} auth={true} />
                  }
                />
                <Route
                  path="/user-management"
                  element={
                    <GuardedRoute element={<UserManagementView readOnly={readOnly} />} auth={true} />
                  }
                />
                <Route
                  path="/admin-billing"
                  element={
                    <GuardedRoute element={<UnifiedAdminBilling />} auth={true} />
                  }
                />
                <Route
                  path="/blueticks-api"
                  element={
                    <GuardedRoute element={<AdminAPIView />} auth={true} />
                  }
                />
                <Route
                  path="/refer"
                  element={
                    <GuardedRoute element={<ReferView />} auth={true} />
                  }
                />
                <Route
                  path="/credits-usage"
                  element={
                    <GuardedRoute element={<CreditsUsageView />} auth={true} />
                  }
                />
                <Route
                  path="/login"
                  element={
                    <GuardedRoute
                      element={<LoginView initialState={FormState.Login} />}
                      redirect='/calendar'
                      auth={false}
                      redirectBack={false}
                    />
                  } />
                <Route path="/signup" element={
                  <GuardedRoute
                    element={<LoginView initialState={FormState.Signup} />}
                    redirect='/calendar'
                    auth={false}
                  />
                } />
                <Route path="/fyp" element={
                  <GuardedRoute
                    element={<LoginView initialState={FormState.ForgotPassword} />}
                    redirect='/calendar'
                    auth={false}
                  />
                } />
                {/* Clear-flow auth screens: identical forms, landing on /clear-calendar. */}
                <Route
                  path="/clear-login"
                  element={
                    <GuardedRoute
                      element={<LoginView initialState={FormState.Login} />}
                      redirect='/clear-calendar'
                      auth={false}
                      redirectBack={false}
                    />
                  } />
                <Route path="/clear-signup" element={
                  <GuardedRoute
                    element={<LoginView initialState={FormState.Signup} />}
                    redirect='/clear-calendar'
                    auth={false}
                  />
                } />
                <Route path="/clear-fyp" element={
                  <GuardedRoute
                    element={<LoginView initialState={FormState.ForgotPassword} />}
                    redirect='/clear-calendar'
                    auth={false}
                  />
                } />
                <Route path="/pricing" element={
                  <GuardedRoute
                    element={<SubscriptionView />}
                    auth={true}
                  />
                } />
                <Route path="/tasks" element={
                  <GuardedRoute
                    element={<TaskView />}
                    auth={true}
                  />
                } />
                <Route path="/mywork" element={
                  <GuardedRoute
                    element={<TaskView />}
                    auth={true}
                  />
                } />
                <Route
                  path="/k8s-analyzer"
                  element={
                    <GuardedRoute
                      element={<K8sAnalyzer />}
                      auth={true}
                      requiredRole="admin"
                    />
                  }
                />
                <Route
                  path="/k8s-analyzer-nodes"
                  element={
                    <GuardedRoute
                      element={<K8sAnalyzerNodesView />}
                      auth={true}
                      requiredRole="admin"
                    />
                  }
                />



                <Route
                  path="/reset-password"
                  element={<ResetPasswordView />}
                />
                <Route
                  path="/account-language"
                  element={
                    <GuardedRoute element={<AccountLangView />} auth={true} />
                  }
                />
                <Route
                  path="/forbidden-access"
                  element={<ForbiddenAccessView />}
                />
                <Route
                  path="/account-notification"
                  element={
                    <GuardedRoute
                      element={<AccountNotificationsView />}
                      auth={true}
                    />
                  }
                />
                <Route
                  path="/account-security"
                  element={
                    <GuardedRoute element={<AccountSecurityView />} auth={true} />
                  }
                />
                <Route
                  path="/account-backup"
                  element={
                    <GuardedRoute element={<AccountBackupView />} auth={true} />
                  }
                />
                <Route
                  path="/thank-you"
                  element={<ThankYouView />}
                />
                <Route
                  path="/account-workspace"
                  element={
                    <GuardedRoute
                      element={<AccountWorkspacesView readOnly={readOnly} />}
                      auth={true}
                    />
                  }
                />
                <Route
                  path="/trialModal"
                  element={<TrialModal />}
                />
                <Route
                  path="/qr-view"
                  element={
                    <GuardedRoute
                      element={<QrView />}
                      auth={true}
                    />
                  }
                />
                <Route
                  path="/support"
                  element={<GuardedRoute element={<Suspense fallback={null}><Support /></Suspense>} auth={true} />}
                />
                <Route
                  path="/bridges"
                  element={<GuardedRoute element={<Suspense fallback={null}><BridgesPage /></Suspense>} auth={true} requiredRole="admin" />}
                />
                <Route
                  path="/notifications-drawer"
                  element={
                    <GuardedRoute element={<NotificationsDrawer />} auth={true} />
                  }
                />
                <Route path="/api-documentation" element={<ApiDocumentationView />} />
                <Route path="/webhook-guide" element={<WebHookGuideView />} />
                <Route path="/webhook-make-guide" element={<WebHookGuideMake />} />
                <Route path="/webhook-zapier-guide" element={<WebHookGuideZapier />} />
                <Route path="/thank-you" element={<ThankYouView />} />
                <Route path="/uninstall" element={<UninstallView />} />
                <Route path="/subscription-plans-demo" element={<AllPlansDemo />} />
                <Route path="/sharetable" element={<InviteUserModal />} />
                <Route path="/editor-playground" element={<EditorPlayground />} />
                <Route
                  path="/whatsapp-view"
                  element={
                    <GuardedRoute
                      element={<WhatsappView />}
                      auth={true}
                      requiredRole="admin"
                    />
                  }
                />
                <Route
                  path="/view-backup"
                  element={
                    <GuardedRoute
                      element={<ViewBackupView />}
                      auth={true}
                      requiredRole="admin"
                    />
                  }
                />
                <Route
                  path="/join-task-board/:shareId"
                  element={
                    <GuardedRoute element={<JoinTaskBoard />} auth={true} />
                  }
                />
                <Route
                  path="/invoices/:invoiceId"
                  element={
                    <GuardedRoute element={<InvoiceView />} auth={true} />
                  }
                />
                <Route path="/join-board/:boardId" element={
                  <GuardedRoute
                    element={<JoinBoard />}
                    auth={true}
                  />
                } />
                <Route path="/join-workspace/:workspaceId" element={
                  <GuardedRoute
                    element={<JoinWorkSpace />}
                    auth={true}
                  />
                } />
                {/* Unknown URL. flowPath keeps a /clear-* visitor on the clear login
                    instead of dropping them into the full product: a typo in the
                    address bar was the one way out of the flow. */}
                <Route path="*" element={<Navigate to={flowPath('/login')} replace />} />

                <Route
                  path="/calendar"
                  element={
                    <GuardedRoute element={<CalendarView />} auth={true} />
                  }
                />
                {/* "Clear" flow — the same Messages Planner rendered chromeless
                    (no sidebar, banners or header), with its own self-contained
                    auth screens so it never links back into the full product. */}
                <Route
                  path="/clear-calendar"
                  element={
                    <GuardedRoute element={<CalendarView bare />} auth={true} loginPath="/clear-login" />
                  }
                />
                <Route
                  path="/clear-account"
                  element={
                    <GuardedRoute element={<AccountView readOnly={readOnly} bare />} auth={true} loginPath="/clear-login" />
                  }
                />
                <Route
                  path="/clear-account-security"
                  element={
                    <GuardedRoute element={<AccountSecurityView bare />} auth={true} loginPath="/clear-login" />
                  }
                />
                <Route
                  path="/clear-account-backup"
                  element={
                    <GuardedRoute element={<AccountBackupView bare />} auth={true} loginPath="/clear-login" />
                  }
                />
                <Route
                  path="/clear-connections"
                  element={
                    <GuardedRoute element={<AccountPage page={<EnginesManagementPage />} bare />} auth={true} loginPath="/clear-login" />
                  }
                />
                <Route
                  path="/bot-build-view/:botId"
                  element={
                    <GuardedRoute element={<BTChatBotView />} auth={true} />
                  }
                />
                <Route
                  path="/bot-list-view"
                  element={
                    <GuardedRoute element={<BotListView />} auth={true} />
                  }
                />
                <Route
                  path="/bot-detail-view/:botId"
                  element={
                    <GuardedRoute element={<BotDetailView />} auth={true} />
                  }
                />
                <Route
                  path="/bot-detail-view/:botId/session/:sessionId"
                  element={
                    <GuardedRoute element={<SessionDetailsView />} auth={true} />
                  }
                />
                <Route
                  path="/bot-test-chat/:botId"
                  element={
                    <GuardedRoute element={<BotTestChatView />} auth={true} />
                  }
                />
                <Route
                  path="/page-not-found"
                  element={<PageNotFound />}
                />
                <Route
                  path="/team-inbox"
                  element={<TeamInboxView />}
                />
                <Route
                  path="/bt-team-inbox"
                  element={
                    <GuardedRoute element={<BtTeamInboxView />} auth={true} />
                  }
                />
                <Route
                  path="/keep-whatsapp-awake"
                  element={
                    <GuardedRoute element={<KeepWhatsappAwakeView />} auth={true} />
                  }
                />
                <Route
                  path="/user-overview"
                  element={
                    <GuardedRoute element={<UserOverview />} auth={true} />
                  }
                />
                <Route
                  path="/engine-explorer/:engineId"
                  element={
                    <GuardedRoute element={<EngineExplorer />} auth={true} />
                  }
                />
                {/* Legacy entry → the route-driven wizard now requires a draft
                    id, so kick off the create-draft flow at /campaigns/new. */}
                <Route path="/launch-campaign" element={
                  <GuardedRoute element={<Navigate to="/campaigns/new" replace />} auth={true} />
                } />
                {/* Deep-linkable campaign-management tabs + audience board (website only).
                    All render the same view; the active tab / board is restored from the URL. */}
                <Route path="/manage-campaigns" element={
                  <GuardedRoute element={<CampaignsRedesignView />} auth={true} />
                } />
                <Route path="/manage-audiences" element={
                  <GuardedRoute element={<LaunchCampaignView />} auth={true} />
                } />
                <Route path="/manage-templates" element={
                  <GuardedRoute element={<LaunchCampaignView />} auth={true} />
                } />
                <Route path="/audience" element={
                  <GuardedRoute element={<LaunchCampaignView />} auth={true} />
                } />
                <Route path="/new-template" element={
                  <GuardedRoute element={<LaunchCampaignView />} auth={true} />
                } />
                {/* Drip Campaigns redesign — fully route-driven (see
                    docs/design-handoffs/drip-redesign-routing.md). The shell
                    derives its surface + step from the route params/path; there
                    is no `surface` prop guessing. Order matters: the literal
                    `/new` and the `/:id/edit|launched` children must precede the
                    bare `/:id` detail route so they aren't shadowed by it. */}
                {/* Shared SSoT path list (see campaigns-routes.tsx) — the same
                    table the extension's MemoryRouter renders, so the two routers
                    can't drift. Each is auth-guarded here. */}
                {CAMPAIGN_REDESIGN_PATHS.map((path) => (
                  <Route key={path} path={path} element={
                    <GuardedRoute element={<CampaignsRedesignView />} auth={true} />
                  } />
                ))}

                {/* Campaigns, audiences and templates inside the white-labelled flow.
                    The base path is written literally because the route/MIRRORED_ROOTS
                    sync test scans this file for it; the children are derived from the
                    canonical table so the two products cannot drift. */}
                <Route path="/clear-campaigns" element={
                  <GuardedRoute element={<CampaignsRedesignView bare />} auth={true} loginPath="/clear-login" />
                } />
                {CLEAR_CAMPAIGN_REDESIGN_PATHS.filter((path) => path !== '/clear-campaigns').map((path) => (
                  <Route key={path} path={path} element={
                    <GuardedRoute element={<CampaignsRedesignView bare />} auth={true} loginPath="/clear-login" />
                  } />
                ))}

                <Route path="/campaigns-management" element={
                  <GuardedRoute element={<CampaignsRedesignView />} auth={true} />
                } />
                <Route
                  path="/session-timeline/:sessionId"
                  element={
                    <GuardedRoute
                      element={<SessionTimeline />}
                      auth={true}
                      requiredRole="admin"
                    />
                  }
                />

                <Route path="/knowledge-base" element={
                  <GuardedRoute
                    element={<KnowledgeBaseView />}
                    auth={true}
                  />
                } />
                <Route path="/knowledge-base/:id" element={
                  <GuardedRoute
                    element={<KnowledgeBaseDetail />}
                    auth={true}
                  />
                } />

                <Route path="/whatsapp-numbers" element={
                  <GuardedRoute
                    element={<AllocatedPhoneNumbersPage />}
                    auth={true}
                  />
                } />

                <Route
                  path="/oauth-test"
                  element={
                    <GuardedRoute element={<OAuthTest />} auth={true} />
                  }
                />

                <Route
                  path="/oauth/mcp"
                  element={
                    <GuardedRoute 
                      element={<MCPOAuthPage />} 
                      auth={true}
                      redirectBack={true}
                    />
                  }
                />

                <Route
                  path="/oauth/callback"
                  element={<MCPOAuthCallbackPage />}
                />

                <Route
                  path="/bot-usage-dashboard"
                  element={
                    <GuardedRoute
                      element={<BotUsageDashboard />}
                      auth={true}
                      requiredRole="admin"
                    />
                  }
                />

                <Route
                  path="/bots"
                  element={
                    <GuardedRoute
                      element={<BotList />}
                      auth={true}
                      requiredRole="admin"
                    />
                  }
                />

                <Route
                  path="/sessions"
                  element={
                    <GuardedRoute
                      element={<SessionListPage />}
                      auth={true}
                      requiredRole="admin"
                    />
                  }
                />
                {/* First run: signup → connect WhatsApp (local or always-on) → first message out.
                    Full-screen on purpose — no sidebar to wander off into. */}
                <Route
                  path="/onboarding"
                  element={<GuardedRoute element={<Suspense fallback={null}><OnboardingPage /></Suspense>} auth={true} />}
                />
                {/* WhatsApp Connections is a main-nav view (under AI MCP) — opens inside
                    AppView like Agents 2.0, not the account-settings shell. */}
                <Route
                  path="/connections"
                  element={
                    <GuardedRoute
                      element={<AppView showHeader={false}><EnginesManagementPage /></AppView>}
                      auth={true}
                    />
                  }
                />
                {/* Agents 2.0 (was "My Bridge" at /my-bridge — old links redirect).
                    ONE route for /agents, /agents/new and /agents/:agentId, so the
                    agent workspace stays mounted from a new agent to the one it
                    creates (the conversation stays on screen). It is a main-nav
                    view like Calendar, so it opens inside AppView (main sidebar
                    stays) — not the account-settings shell. */}
                <Route
                  path="/agents/*"
                  element={
                    <GuardedRoute
                      element={<AppView showHeader={false}><MyBridgePage /></AppView>}
                      auth={true}
                    />
                  }
                />
                <Route path="/my-bridge" element={<Navigate to="/agents" replace />} />
                <Route path="/my-bridge/agents/:agentId" element={<LegacyAgentEditRedirect />} />

                <Route
                  path="/cluster-optimizer"
                  element={
                    <GuardedRoute
                      element={<ClusterOptimizer />}
                      auth={true}
                      requiredRole="admin"
                    />
                  }
                />

                {/* ONE route for the new chat and a saved one: the view stays mounted
                    when the first reply turns /btgpt into /btgpt/:id (no remount, no flash). */}
                <Route
                  path="/btgpt/:id?"
                  element={
                    <GuardedRoute
                      element={<BTGPTView />}
                      auth={true}
                    />
                  }
                />

                <Route
                  path="/import-messages"
                  element={
                    <GuardedRoute
                      element={<ImportMessagesView />}
                      auth={true}
                    />
                  }
                />

                <Route
                  path="/ai-assistant"
                  element={
                    <GuardedRoute
                      element={<AiAssistant />}
                      auth={true}
                    />
                  }
                />

                <Route
                  path="/test-components"
                  element={
                    <GuardedRoute
                      element={<TestComponentsView />}
                      auth={true}
                    />
                  }
                />
                <Route
                  path="/test-reminder"
                  element={
                    <GuardedRoute
                      element={<TestReminderPage />}
                      auth={true}
                    />
                  }
                />
                <Route
                  path="/messages-dashboard"
                  element={
                    <GuardedRoute
                      element={<MessagesDashboard />}
                      auth={true}
                      requiredRole="admin"
                    />
                  }
                />
                <Route
                  path="/diagnostics"
                  element={
                    <GuardedRoute
                      element={<DiagnosticsView />}
                      auth={true}
                    />
                  }
                />
                <Route
                  path="/online-engines"
                  element={
                    <GuardedRoute
                      element={<OnlineEnginesView />}
                      auth={true}
                    />
                  }
                />

              </Routes>
              <ConditionalWrapper shouldLoadSelector={(state: CommonRootState) => !!state.PaymentFormReducer.paymentSuccess}>
                <PaymentThankYouPopup />
              </ConditionalWrapper>
              {/* Clear-flow side effects (hides the Crisp bubble). Inside the router
                  so it also covers the clear auth screens, which have no AppView. */}
              <ClearFlowChrome />
            </HistoryRouter>
          </div>
          {/* Single sonner Toaster for the web SPA root */}
          <Toaster />
        </BTThemeProvider>
      </HelmetProvider>
    );
  };

  return sitePages();
}
